import {
    Add,
    ArrowUpward,
    PersonAddAlt1,
    Remove,
    Search,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    Switch,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import SimpleWidgetsHeader from "../../../../components/SimpleWidgetsHeader/SimpleWidgetsHeader";
import {
    useCreateDoctypeData,
    useGetIncomingRate,
} from "../../../../shared/hooks/useDocTypesData";
import { useGetParyDetails } from "../../../../shared/hooks/usePOSData";
import { useStyles } from "./styles";

// Import refactored modules
import { EmployeeAdvanceTab, ExpenseTab } from "./components";
import { CASH_OUT_TABS } from "./constants";
import { useCashOutData, useCashOutForm } from "./hooks";
import {
    calculateItemAmount,
    createExpenseJournalEntry,
    isFormValid,
    roundToDecimalPlaces,
    storeTransaction,
} from "./utils";

const CashOutDialog = ({
    dialogOpen,
    onConfirm,
    onCancel,
    customerSelected,
    selectedCustomer,
}) => {
    const classes = useStyles();
    const [submitLoading, setSubmitLoading] = useState(false);

    // Use custom hooks for state management and data fetching
    const formState = useCashOutForm();
    const dataState = useCashOutData();

    // Set default employee when employees are loaded
    useEffect(() => {
        if (dataState.currentEmployee && !formState.selectedEmployee) {
            formState.setSelectedEmployee(dataState.currentEmployee);
        }
    }, [
        dataState.currentEmployee,
        formState.selectedEmployee,
        formState.setSelectedEmployee,
    ]);

    const createExpenseClaimMutation = useCreateDoctypeData();
    const createStockEntryMutation = useCreateDoctypeData();
    const createEmployeeAdvanceMutation = useCreateDoctypeData();
    const createPaymentEntryMutation = useCreateDoctypeData();
    const createItemMutation = useCreateDoctypeData();

    // Get incoming rate mutation
    const getIncomingRateMutation = useGetIncomingRate();

    // Get party details mutation
    const getPartyDetailsMutation = useGetParyDetails();

    // Fetch supplier balance when supplier changes and payment mode is enabled
    useEffect(() => {
        const fetchSupplierBalance = async () => {
            if (
                formState.selectedSupplier &&
                formState.isPaymentMode &&
                !formState.fetchingPartyDetails
            ) {
                try {
                    formState.setFetchingPartyDetails(true);
                    const currentDate = new Date().toISOString().split("T")[0];
                    const response = await getPartyDetailsMutation.mutateAsync({
                        company:
                            dataState.company.name ||
                            dataState.company.company_name,
                        party_type: "Supplier",
                        party: formState.selectedSupplier,
                        date: currentDate,
                    });
                    formState.setSupplierBalance(response.data?.message);
                } catch (error) {
                    console.error("Error fetching supplier balance:", error);
                    formState.setSupplierBalance(null);
                } finally {
                    formState.setFetchingPartyDetails(false);
                }
            } else if (
                !formState.selectedSupplier ||
                !formState.isPaymentMode
            ) {
                formState.setSupplierBalance(null);
                formState.setFetchingPartyDetails(false);
            }
        };

        fetchSupplierBalance();
    }, [
        formState.selectedSupplier,
        formState.isPaymentMode,
        dataState.company.name,
        dataState.company.company_name,
    ]);

    // Auto-update purchase paid amount when total changes
    useEffect(() => {
        if (!formState.isPaymentMode && formState.stockEntryTotal > 0) {
            formState.setPurchasePaidAmount(
                formState.stockEntryTotal.toString()
            );
        }
    }, [formState.stockEntryTotal, formState.isPaymentMode]);

    const handleOnClose = () => {
        formState.resetForm();
        if (onCancel) onCancel();
    };

    // Stock Entry functions
    const addItemToStockEntry = async (item) => {
        const existingItem = formState.selectedItems.find(
            (si) => si.item_code === item.item_code
        );

        if (existingItem) {
            formState.setSelectedItems((prev) =>
                prev.map((si) =>
                    si.item_code === item.item_code
                        ? {
                              ...si,
                              qty: si.qty + 1,
                              amount: calculateItemAmount(si.qty + 1, si.rate),
                          }
                        : si
                )
            );
        } else {
            // Generate unique voucher for rate fetching
            const voucherNo = `new-stock-entry-detail-${Math.random()
                .toString(36)
                .substr(2, 10)}`;

            const currentDate = new Date();
            const postingDate = currentDate.toISOString().split("T")[0];
            const postingTime = currentDate.toTimeString().split(" ")[0];

            // Create new item with default rate first
            const newItem = {
                item_code: item.item_code,
                item_name: item.item_name,
                qty: 1,
                rate: item.standard_rate || 0,
                uom: item.stock_uom || "Unit",
                amount: item.standard_rate || 0,
            };

            // Add item to list immediately
            formState.setSelectedItems((prev) => [...prev, newItem]);

            // Fetch current rate in background
            try {
                const rateArgs = {
                    item_code: item.item_code,
                    posting_date: postingDate,
                    posting_time: postingTime,
                    warehouse:
                        dataState.company.default_warehouse ||
                        dataState.company.warehouse ||
                        `Stores - ${dataState.company.abbr}`,
                    company:
                        dataState.company.name ||
                        dataState.company.company_name,
                    qty: 0,
                    voucher_type: "Stock Entry",
                    voucher_no: voucherNo,
                    allow_zero_valuation: 1,
                };

                const currentRate = await getIncomingRateMutation.mutateAsync(
                    rateArgs
                );

                // Update item with fetched rate (rounded to 2 decimal places)
                if (currentRate && currentRate > 0) {
                    const roundedRate = roundToDecimalPlaces(currentRate);
                    formState.setSelectedItems((prev) =>
                        prev.map((si) =>
                            si.item_code === item.item_code
                                ? {
                                      ...si,
                                      rate: roundedRate,
                                      amount: calculateItemAmount(
                                          si.qty,
                                          roundedRate
                                      ),
                                  }
                                : si
                        )
                    );
                }
            } catch (error) {
                console.warn(
                    `Could not fetch rate for ${item.item_code}:`,
                    error
                );
                // Keep using standard_rate if API call fails
            }
        }
    };

    const updateItemQty = (itemCode, newQty) => {
        // Allow empty values to stay empty for better UX
        // Only convert to number for calculation purposes
        const qtyForCalculation = newQty === "" ? 0 : parseFloat(newQty) || 0;

        formState.setSelectedItems((prev) =>
            prev.map((si) =>
                si.item_code === itemCode
                    ? {
                          ...si,
                          qty: newQty, // Keep original value (including empty string)
                          amount: calculateItemAmount(
                              qtyForCalculation,
                              parseFloat(si.rate) || 0
                          ),
                      }
                    : si
            )
        );
    };

    const updateItemRate = (itemCode, newRate) => {
        // Allow empty values to stay empty for better UX
        // Only convert to number for calculation purposes
        const rateForCalculation =
            newRate === "" ? 0 : parseFloat(newRate) || 0;

        formState.setSelectedItems((prev) =>
            prev.map((si) =>
                si.item_code === itemCode
                    ? {
                          ...si,
                          rate: newRate, // Keep original value (including empty string)
                          amount: calculateItemAmount(
                              parseFloat(si.qty) || 0,
                              rateForCalculation
                          ),
                      }
                    : si
            )
        );
    };

    const removeItemFromStockEntry = (itemCode) => {
        formState.setSelectedItems((prev) =>
            prev.filter((si) => si.item_code !== itemCode)
        );
    };

    // Item creation function
    const handleCreateItem = async () => {
        if (
            !formState.newItemData.item_name ||
            !formState.newItemData.item_group ||
            !formState.newItemData.purchase_rate
        ) {
            enqueueSnackbar("Please fill all required fields", {
                variant: "error",
            });
            return;
        }

        formState.setCreatingItem(true);
        try {
            // Generate unique document name (simplified version)
            const itemName = `new-item-${Math.random()
                .toString(36)
                .substr(2, 10)}`;
            const purchaseRate = parseFloat(
                formState.newItemData.purchase_rate
            );

            // Create simplified form data structure
            const formData = {
                docstatus: 0,
                doctype: "Item",
                name: itemName,
                __islocal: 1,
                __unsaved: 1,
                owner: dataState.userInfo.name,
                naming_series: "STO-ITEM-.YYYY.-",
                item_code: formState.newItemData.item_name,
                item_name: formState.newItemData.item_name,
                item_group: formState.newItemData.item_group,
                stock_uom: "Nos",
                is_purchase_item: 1,
                valuation_rate: purchaseRate,
                standard_rate: purchaseRate,
                // ... other required fields would be here in real implementation
            };

            await createItemMutation.mutateAsync({
                doctype: "Item",
                formData: formData,
            });

            // Add the newly created item to the purchase list
            const newItem = {
                item_code: formState.newItemData.item_name,
                item_name: formState.newItemData.item_name,
                standard_rate: purchaseRate,
                stock_uom: "Nos",
            };

            await addItemToStockEntry(newItem);

            // Reset form and close modal
            formState.setNewItemData({
                item_name: "",
                item_group: "",
                purchase_rate: "",
            });
            formState.setShowCreateItemModal(false);
            enqueueSnackbar("Item created and added successfully!", {
                variant: "success",
            });
        } catch (error) {
            console.error("Error creating item:", error);
            enqueueSnackbar(`Error creating item: ${error.message}`, {
                variant: "error",
            });
        } finally {
            formState.setCreatingItem(false);
        }
    };

    const filteredItems = useMemo(() => {
        return dataState.getFilteredItems(formState.itemSearchText);
    }, [dataState.getFilteredItems, formState.itemSearchText]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitLoading(true);

        try {
            let transactionData = {};

            if (formState.currentTab === 0) {
                // Expense - Journal Entry
                const expenseAmount = parseFloat(formState.amount);

                const formData = createExpenseJournalEntry(
                    dataState.userInfo,
                    dataState.company,
                    expenseAmount,
                    formState.description,
                    formState.selectedExpenseAccount
                );

                await createExpenseClaimMutation.mutateAsync({
                    doctype: "Journal Entry",
                    formData: { ...formData, docstatus: 1 },
                });

                transactionData = {
                    type: "expense",
                    amount: expenseAmount,
                    description: formState.description,
                };

                enqueueSnackbar(`Expense Journal Entry Submitted!`);
            } else if (formState.currentTab === 1) {
                if (formState.isPaymentMode) {
                    // Payment Entry for supplier payment
                    const paymentAmountValue = parseFloat(
                        formState.paymentAmount
                    );
                    const currentDate = new Date().toISOString().split("T")[0];

                    // Generate unique document name
                    const paymentEntryName = `new-payment-entry-${Math.random()
                        .toString(36)
                        .substr(2, 10)}`;

                    const formData = {
                        docstatus: 1,
                        doctype: "Payment Entry",
                        name: paymentEntryName,
                        __islocal: 1,
                        __unsaved: 1,
                        owner: dataState.userInfo.name,
                        naming_series: "ACC-PAY-.YYYY.-",
                        payment_type: "Pay",
                        payment_order_status: "Initiated",
                        posting_date: currentDate,
                        company:
                            dataState.company.name ||
                            dataState.company.company_name,
                        mode_of_payment: "Cash",
                        book_advance_payments_in_separate_party_account: 0,
                        paid_from_account_currency:
                            dataState.company.default_currency || "ILS",
                        paid_to_account_currency:
                            dataState.company.default_currency || "ILS",
                        references: [],
                        apply_tax_withholding_amount: 0,
                        taxes: [],
                        deductions: [],
                        status: "Draft",
                        custom_remarks: 0,
                        letter_head: "Default",
                        source_exchange_rate: 1,
                        paid_to:
                            formState.supplierBalance?.party_account ||
                            `Creditors - ${dataState.company.abbr}`,
                        target_exchange_rate: 1,
                        paid_to_account_balance:
                            formState.supplierBalance?.party_balance || 0,
                        paid_to_account_type: "Payable",
                        party_type: "Supplier",
                        paid_from:
                            // dataState.company.default_cash_account ||
                            `Petty Cash - ${dataState.company.abbr}`,
                            // `Cash - ${dataState.company.abbr}`,
                        paid_from_account_type: "Cash",
                        paid_from_account_balance: 0, // Will be calculated by system
                        base_paid_amount: paymentAmountValue,
                        base_received_amount: paymentAmountValue,
                        unallocated_amount: paymentAmountValue,
                        difference_amount: 0,
                        party: formState.selectedSupplier,
                        party_balance:
                            formState.supplierBalance?.party_balance || 0,
                        party_name:
                            formState.supplierBalance?.party_name ||
                            formState.selectedSupplier,
                        paid_amount: paymentAmountValue,
                        received_amount: paymentAmountValue,
                        total_allocated_amount: 0,
                        base_total_allocated_amount: 0,
                    };

                    await createPaymentEntryMutation.mutateAsync({
                        doctype: "Payment Entry",
                        formData: formData,
                    });

                    transactionData = {
                        type: "supplier_payment",
                        amount: paymentAmountValue,
                        description: `Payment to ${
                            formState.supplierBalance?.party_name ||
                            formState.selectedSupplier
                        }`,
                    };

                    enqueueSnackbar(`Supplier Payment Submitted!`);
                } else {
                    // Purchase Invoice for items (existing logic)
                    const currentDate = new Date().toISOString().split("T")[0];
                    const currentTime = new Date().toTimeString().split(" ")[0];

                    // Generate unique document name
                    const purchaseInvoiceName = `new-purchase-invoice-${Math.random()
                        .toString(36)
                        .substr(2, 10)}`;

                    // Calculate total amount
                    const totalAmount = formState.stockEntryTotal;
                    const paidAmountValue =
                        parseFloat(formState.purchasePaidAmount) || 0;
                    const outstandingAmount = totalAmount - paidAmountValue;
                    const isFullyPaid = outstandingAmount <= 0.01; // Account for floating point precision

                    const formData = {
                        docstatus: 0,
                        doctype: "Purchase Invoice",
                        name: purchaseInvoiceName,
                        __islocal: 1,
                        __unsaved: 1,
                        owner: dataState.userInfo.name,
                        naming_series: "ACC-PINV-.YYYY.-",
                        supplier: formState.selectedSupplier,
                        supplier_name:
                            dataState.suppliers?.find(
                                (s) => s.name === formState.selectedSupplier
                            )?.supplier_name || formState.selectedSupplier,
                        company:
                            dataState.company.name ||
                            dataState.company.company_name,
                        posting_date: currentDate,
                        posting_time: currentTime,
                        set_posting_time: 1,
                        due_date: currentDate,
                        is_paid: paidAmountValue > 0 ? 1 : 0,
                        is_return: 0,
                        apply_tds: 0,
                        currency: dataState.company.default_currency || "ILS",
                        conversion_rate: 1,
                        use_transaction_date_exchange_rate: 0,
                        buying_price_list: "Standard Buying",
                        price_list_currency:
                            dataState.company.default_currency || "ILS",
                        plc_conversion_rate: 1,
                        ignore_pricing_rule: 0,
                        update_stock: 1,
                        is_subcontracted: 0,
                        total_qty: formState.selectedItems.reduce(
                            (sum, item) => sum + item.qty,
                            0
                        ),
                        total_net_weight: 0,
                        base_total: totalAmount,
                        base_net_total: totalAmount,
                        total: totalAmount,
                        net_total: totalAmount,
                        tax_withholding_net_total: 0,
                        base_tax_withholding_net_total: 0,
                        tax_category: "",
                        base_taxes_and_charges_added: 0,
                        base_taxes_and_charges_deducted: 0,
                        base_total_taxes_and_charges: 0,
                        taxes_and_charges_added: 0,
                        taxes_and_charges_deducted: 0,
                        total_taxes_and_charges: 0,
                        base_grand_total: totalAmount,
                        base_rounding_adjustment: 0,
                        base_rounded_total: totalAmount,
                        grand_total: totalAmount,
                        rounding_adjustment: 0,
                        rounded_total: totalAmount,
                        total_advance: 0,
                        outstanding_amount: outstandingAmount,
                        disable_rounded_total: 0,
                        apply_discount_on: "Grand Total",
                        base_discount_amount: 0,
                        additional_discount_percentage: 0,
                        discount_amount: 0,
                        mode_of_payment: paidAmountValue > 0 ? "Cash" : null,
                        cash_bank_account:
                            paidAmountValue > 0
                                ? dataState.company.default_cash_account ||
                                  `Cash - ${dataState.company.abbr}`
                                : null,
                        base_paid_amount: paidAmountValue,
                        paid_amount: paidAmountValue,
                        allocate_advances_automatically: 0,
                        only_include_allocated_payments: 0,
                        write_off_amount: 0,
                        base_write_off_amount: 0,
                        change_amount: 0,
                        base_change_amount: 0,
                        credit_to:
                            dataState.company.default_payable_account ||
                            `Creditors - ${dataState.company.abbr}`,
                        party_account_currency:
                            dataState.company.default_currency || "ILS",
                        is_opening: "No",
                        repost_required: 0,
                        letter_head: "Default",
                        group_same_items: 0,
                        language: "en",
                        on_hold: 0,
                        is_internal_supplier: 0,
                        represents_company: "",
                        is_old_subcontracting_flow: 0,
                        remarks: formState.description || "Purchase from POS",
                        status: isFullyPaid ? "Paid" : "Unpaid",
                        ignore_default_payment_terms_template: 0,
                        advances: [],
                        advance_tax: [],
                        payment_schedule: [],
                        tax_withheld_vouchers: [],
                        pricing_rules: [],
                        supplied_items: [],
                        taxes: [],
                        set_warehouse:
                            dataState.company.default_warehouse ||
                            dataState.company.warehouse ||
                            `Stores - ${dataState.company.abbr}`,
                        items: formState.selectedItems.map((item, index) => ({
                            docstatus: 0,
                            doctype: "Purchase Invoice Item",
                            name: `new-purchase-invoice-item-${Math.random()
                                .toString(36)
                                .substr(2, 10)}`,
                            __islocal: 1,
                            __unsaved: 1,
                            owner: dataState.userInfo.name,
                            idx: index + 1,
                            item_code: item.item_code,
                            item_name: item.item_name,
                            description: item.item_name,
                            item_group: "All Item Groups",
                            received_qty: 0,
                            qty: item.qty,
                            rejected_qty: 0,
                            uom: item.uom,
                            conversion_factor: 1,
                            stock_uom: item.uom,
                            stock_qty: item.qty,
                            price_list_rate: 0,
                            base_price_list_rate: 0,
                            margin_type: "",
                            margin_rate_or_amount: 0,
                            rate_with_margin: 0,
                            discount_percentage: 0,
                            discount_amount: 0,
                            base_rate_with_margin: 0,
                            rate: item.rate,
                            amount: item.amount,
                            base_rate: item.rate,
                            base_amount: item.amount,
                            pricing_rules: "",
                            stock_uom_rate: item.rate,
                            is_free_item: 0,
                            apply_tds: 0,
                            net_rate: item.rate,
                            net_amount: item.amount,
                            base_net_rate: item.rate,
                            base_net_amount: item.amount,
                            valuation_rate: 0,
                            item_tax_amount: 0,
                            landed_cost_voucher_amount: 0,
                            rm_supp_cost: 0,
                            warehouse:
                                dataState.company.default_warehouse ||
                                dataState.company.warehouse ||
                                `Stores - ${dataState.company.abbr}`,
                            use_serial_batch_fields: 0,
                            expense_account:
                                dataState.company.default_expense_account ||
                                `Cost of Goods Sold - ${dataState.company.abbr}`,
                            is_fixed_asset: 0,
                            enable_deferred_expense: 0,
                            allow_zero_valuation_rate: 0,
                            item_tax_rate: "{}",
                            include_exploded_items: 0,
                            weight_per_unit: 0,
                            total_weight: 0,
                            cost_center:
                                dataState.company.cost_center ||
                                `Main - ${dataState.company.abbr}`,
                            page_break: 0,
                            update_stock: 1,
                            parent: purchaseInvoiceName,
                            parentfield: "items",
                            parenttype: "Purchase Invoice",
                        })),
                    };

                    await createStockEntryMutation.mutateAsync({
                        doctype: "Purchase Invoice",
                        formData: { ...formData, docstatus: 1 },
                    });

                    transactionData = {
                        type: "purchase",
                        amount: paidAmountValue,
                        description: `Purchase from ${
                            dataState.suppliers?.find(
                                (s) => s.name === formState.selectedSupplier
                            )?.supplier_name || formState.selectedSupplier
                        }${
                            outstandingAmount > 0.01
                                ? ` (Partial: ₪${paidAmountValue.toFixed(
                                      2
                                  )}/₪${totalAmount.toFixed(2)})`
                                : ""
                        }`,
                    };

                    enqueueSnackbar(
                        `Purchase Invoice Submitted! ${
                            isFullyPaid
                                ? "Fully Paid"
                                : `Partial Payment: ₪${paidAmountValue.toFixed(
                                      2
                                  )}/₪${totalAmount.toFixed(2)}`
                        }`
                    );
                }
            } else if (formState.currentTab === 2) {
                // Employee Advance - Journal Entry
                const advanceAmountValue = parseFloat(formState.advanceAmount);
                const currentDate = new Date().toISOString().split("T")[0];

                // Generate unique document name
                const journalEntryName = `new-journal-entry-${Math.random()
                    .toString(36)
                    .substr(2, 10)}`;
                const cashAccountName = `new-journal-entry-account-${Math.random()
                    .toString(36)
                    .substr(2, 10)}`;
                const payrollAccountName = `new-journal-entry-account-${Math.random()
                    .toString(36)
                    .substr(2, 10)}`;

                const formData = {
                    docstatus: 0,
                    doctype: "Journal Entry",
                    name: journalEntryName,
                    __islocal: 1,
                    __unsaved: 1,
                    owner: dataState.userInfo.name,
                    is_system_generated: 0,
                    voucher_type: "Journal Entry",
                    naming_series: "ACC-JV-.YYYY.-",
                    company:
                        dataState.company.name ||
                        dataState.company.company_name,
                    apply_tds: 0,
                    multi_currency: 0,
                    total_amount_currency:
                        dataState.company.default_currency || "ILS",
                    write_off_based_on: "Accounts Receivable",
                    letter_head: "Default",
                    is_opening: "No",
                    posting_date: currentDate,
                    total_debit: advanceAmountValue,
                    total_credit: advanceAmountValue,
                    difference: 0,
                    user_remark: formState.advanceReason,
                    accounts: [
                        {
                            docstatus: 0,
                            doctype: "Journal Entry Account",
                            name: cashAccountName,
                            __islocal: 1,
                            __unsaved: 1,
                            owner: dataState.userInfo.name,
                            cost_center:
                                dataState.company.cost_center ||
                                `Main - ${dataState.company.abbr}`,
                            account_currency:
                                dataState.company.default_currency || "ILS",
                            reference_type: "",
                            is_advance: "No",
                            parent: journalEntryName,
                            parentfield: "accounts",
                            parenttype: "Journal Entry",
                            idx: 1,
                            account:
                                // dataState.company.default_cash_account ||
                                `Petty Cash - ${dataState.company.abbr}`,
                                // `Cash - ${dataState.company.abbr}`,
                            party_type: "",
                            account_type: "Cash",
                            exchange_rate: 1,
                            party: "",
                            debit: 0,
                            // debit_in_account_currency: advanceAmountValue,
                            credit: advanceAmountValue,
                            credit_in_account_currency: advanceAmountValue,
                        },
                        {
                            docstatus: 0,
                            doctype: "Journal Entry Account",
                            name: payrollAccountName,
                            __islocal: 1,
                            __unsaved: 1,
                            owner: dataState.userInfo.name,
                            cost_center:
                                dataState.company.cost_center ||
                                `Main - ${dataState.company.abbr}`,
                            account_currency:
                                dataState.company.default_currency || "ILS",
                            reference_type: "",
                            is_advance: "No",
                            parent: journalEntryName,
                            parentfield: "accounts",
                            parenttype: "Journal Entry",
                            idx: 2,
                            __unedited: false,
                            account: `Payroll Payable - ${dataState.company.abbr}`,
                            party_type: "Employee",
                            account_type: "Payable",
                            exchange_rate: 1,
                            party: formState.selectedEmployee,
                            debit: advanceAmountValue,
                            debit_in_account_currency: advanceAmountValue,
                            credit: 0,
                            // credit_in_account_currency: advanceAmountValue,
                        },
                    ],
                };

                await createEmployeeAdvanceMutation.mutateAsync({
                    doctype: "Journal Entry",
                    formData: { ...formData, docstatus: 1 },
                });

                transactionData = {
                    type: "employee_advance",
                    amount: advanceAmountValue,
                    description: formState.advanceReason,
                };

                enqueueSnackbar(`Employee Advance Journal Entry Submitted!`);
            }

            // Store transaction locally for POS closing
            storeTransaction(
                transactionData,
                dataState.userInfo,
                dataState.company
            );

            if (onConfirm) {
                onConfirm(transactionData);
            }

            handleOnClose();
        } catch (error) {
            enqueueSnackbar(`Error: ${error.message}`, {
                variant: "error",
            });
        } finally {
            setSubmitLoading(false);
        }
    };

    let title = "Cash Out";
    if (selectedCustomer) {
        title += ` [ ${selectedCustomer} ]`;
    }

    const getTabIcon = (index) => {
        const tab = CASH_OUT_TABS.find((t) => t.id === index);
        return tab ? tab.icon : PersonAddAlt1;
    };

    const formValid = isFormValid(
        formState.currentTab,
        formState.getFormData()
    );

    return (
        <Dialog
            fullWidth={true}
            maxWidth={"md"}
            open={dialogOpen}
            onClose={handleOnClose}
        >
            <div className={classes.DialogWrapper}>
                <SimpleWidgetsHeader
                    Icon={getTabIcon(formState.currentTab)}
                    onClose={handleOnClose}
                    title={title}
                />

                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                    <Tabs
                        value={formState.currentTab}
                        onChange={formState.handleTabChange}
                    >
                        {CASH_OUT_TABS.map((tab) => (
                            <Tab
                                key={tab.id}
                                icon={<tab.icon />}
                                label={tab.label}
                            />
                        ))}
                    </Tabs>
                </Box>

                <div className={classes.List}>
                    <Box component="form" onSubmit={handleSubmit}>
                        {/* Expense Tab */}
                        {formState.currentTab === 0 && (
                            <ExpenseTab
                                description={formState.description}
                                setDescription={formState.setDescription}
                                amount={formState.amount}
                                setAmount={formState.setAmount}
                                selectedExpenseAccount={
                                    formState.selectedExpenseAccount
                                }
                                setSelectedExpenseAccount={
                                    formState.setSelectedExpenseAccount
                                }
                                expenseAccounts={dataState.expenseAccounts}
                            />
                        )}

                        {/* Purchases and Payments Tab */}
                        {formState.currentTab === 1 && (
                            <>
                                <Autocomplete
                                    options={dataState.suppliers || []}
                                    getOptionLabel={(option) =>
                                        option.supplier_name || option.name
                                    }
                                    value={
                                        dataState.suppliers?.find(
                                            (supplier) =>
                                                supplier.name ===
                                                formState.selectedSupplier
                                        ) || null
                                    }
                                    onChange={(event, newValue) => {
                                        formState.setSelectedSupplier(
                                            newValue?.name || ""
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Supplier"
                                            variant="standard"
                                            margin="normal"
                                            required
                                            fullWidth
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Typography variant="body2">
                                                {option.supplier_name ||
                                                    option.name}
                                            </Typography>
                                        </Box>
                                    )}
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formState.isPaymentMode}
                                            onChange={(e) => {
                                                formState.setIsPaymentMode(
                                                    e.target.checked
                                                );
                                                if (!e.target.checked) {
                                                    formState.setPaymentAmount(
                                                        ""
                                                    );
                                                    formState.setSupplierBalance(
                                                        null
                                                    );
                                                }
                                            }}
                                            disabled={
                                                !formState.selectedSupplier
                                            }
                                        />
                                    }
                                    label="Payment Mode (Pay Supplier)"
                                    sx={{ mt: 2, mb: 1 }}
                                />

                                {formState.isPaymentMode ? (
                                    <>
                                        {formState.fetchingPartyDetails && (
                                            <Alert
                                                severity="info"
                                                sx={{ mt: 1, mb: 2 }}
                                            >
                                                <Typography variant="body2">
                                                    Loading supplier balance...
                                                </Typography>
                                            </Alert>
                                        )}

                                        {formState.supplierBalance &&
                                            !formState.fetchingPartyDetails && (
                                                <Alert
                                                    severity="info"
                                                    sx={{ mt: 1, mb: 2 }}
                                                >
                                                    <Typography variant="body2">
                                                        <strong>
                                                            Supplier:
                                                        </strong>{" "}
                                                        {
                                                            formState
                                                                .supplierBalance
                                                                .party_name
                                                        }
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>
                                                            Current Balance:
                                                        </strong>{" "}
                                                        ₪
                                                        {Math.abs(
                                                            formState
                                                                .supplierBalance
                                                                .party_balance
                                                        ).toFixed(2)}
                                                        {formState
                                                            .supplierBalance
                                                            .party_balance < 0
                                                            ? " (Outstanding)"
                                                            : " (Credit)"}
                                                    </Typography>
                                                </Alert>
                                            )}

                                        <TextField
                                            label="Payment Amount"
                                            type="number"
                                            value={formState.paymentAmount}
                                            onChange={(e) =>
                                                formState.setPaymentAmount(
                                                    e.target.value
                                                )
                                            }
                                            fullWidth
                                            margin="normal"
                                            variant="standard"
                                            inputProps={{ min: 0, step: 0.01 }}
                                            required
                                            disabled={
                                                formState.fetchingPartyDetails
                                            }
                                            helperText="Amount to pay to supplier"
                                        />
                                    </>
                                ) : (
                                    <>
                                        <TextField
                                            label="Description"
                                            value={formState.description}
                                            onChange={(e) =>
                                                formState.setDescription(
                                                    e.target.value
                                                )
                                            }
                                            fullWidth
                                            margin="normal"
                                            variant="standard"
                                            placeholder="Purchase description (optional)"
                                        />

                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 2,
                                                alignItems: "flex-end",
                                                mt: 2,
                                                mb: 1,
                                            }}
                                        >
                                            <Autocomplete
                                                options={filteredItems}
                                                getOptionLabel={(option) =>
                                                    `${option.item_name} (${option.item_code})`
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Add Item"
                                                        variant="standard"
                                                        placeholder="Search and select items..."
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            startAdornment: (
                                                                <>
                                                                    <Search
                                                                        sx={{
                                                                            mr: 1,
                                                                        }}
                                                                    />
                                                                    {
                                                                        params
                                                                            .InputProps
                                                                            .startAdornment
                                                                    }
                                                                </>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                                renderOption={(
                                                    props,
                                                    option
                                                ) => (
                                                    <Box
                                                        component="li"
                                                        {...props}
                                                    >
                                                        <Box>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="medium"
                                                            >
                                                                {
                                                                    option.item_name
                                                                }
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                            >
                                                                {
                                                                    option.item_code
                                                                }{" "}
                                                                • ₪
                                                                {option.standard_rate?.toFixed(
                                                                    2
                                                                ) || "0.00"}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                                onChange={(event, newValue) => {
                                                    if (newValue) {
                                                        addItemToStockEntry(
                                                            newValue
                                                        );
                                                        // Clear search text after adding item
                                                        formState.setItemSearchText(
                                                            ""
                                                        );
                                                    }
                                                }}
                                                value={null}
                                                inputValue={
                                                    formState.itemSearchText
                                                }
                                                onInputChange={(
                                                    event,
                                                    newInputValue
                                                ) => {
                                                    formState.setItemSearchText(
                                                        newInputValue
                                                    );
                                                }}
                                                noOptionsText={
                                                    formState.itemSearchText
                                                        ? `No items found for "${formState.itemSearchText}"`
                                                        : "Type to search items... (search clears automatically after adding)"
                                                }
                                                sx={{ flexGrow: 1 }}
                                                clearOnBlur={false}
                                                blurOnSelect
                                            />

                                            <Tooltip title="Create a new item for purchase">
                                                <Button
                                                    variant="outlined"
                                                    onClick={() =>
                                                        formState.setShowCreateItemModal(
                                                            true
                                                        )
                                                    }
                                                    startIcon={<Add />}
                                                    sx={{
                                                        minWidth: "auto",
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    Create Item
                                                </Button>
                                            </Tooltip>
                                        </Box>

                                        {formState.selectedItems.length > 0 && (
                                            <Card elevation={2} sx={{ mt: 3 }}>
                                                <CardContent sx={{ pb: 1 }}>
                                                    <Typography
                                                        variant="h6"
                                                        color="primary"
                                                        gutterBottom
                                                    >
                                                        Selected Items
                                                    </Typography>
                                                    <TableContainer
                                                        sx={{ mt: 2 }}
                                                    >
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow
                                                                    sx={{
                                                                        bgcolor:
                                                                            "grey.50",
                                                                    }}
                                                                >
                                                                    <TableCell
                                                                        sx={{
                                                                            fontWeight:
                                                                                "bold",
                                                                        }}
                                                                    >
                                                                        Item
                                                                    </TableCell>
                                                                    <TableCell
                                                                        align="center"
                                                                        sx={{
                                                                            fontWeight:
                                                                                "bold",
                                                                            width: 100,
                                                                        }}
                                                                    >
                                                                        Qty
                                                                    </TableCell>
                                                                    <TableCell
                                                                        align="center"
                                                                        sx={{
                                                                            fontWeight:
                                                                                "bold",
                                                                            width: 120,
                                                                        }}
                                                                    >
                                                                        Rate (₪)
                                                                    </TableCell>
                                                                    <TableCell
                                                                        align="right"
                                                                        sx={{
                                                                            fontWeight:
                                                                                "bold",
                                                                            width: 120,
                                                                        }}
                                                                    >
                                                                        Amount
                                                                        (₪)
                                                                    </TableCell>
                                                                    <TableCell
                                                                        align="center"
                                                                        sx={{
                                                                            fontWeight:
                                                                                "bold",
                                                                            width: 80,
                                                                        }}
                                                                    >
                                                                        Actions
                                                                    </TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {formState.selectedItems.map(
                                                                    (
                                                                        item,
                                                                        index
                                                                    ) => (
                                                                        <TableRow
                                                                            key={
                                                                                item.item_code
                                                                            }
                                                                            sx={{
                                                                                "&:nth-of-type(odd)":
                                                                                    {
                                                                                        bgcolor:
                                                                                            "grey.25",
                                                                                    },
                                                                                "&:hover":
                                                                                    {
                                                                                        bgcolor:
                                                                                            "primary.50",
                                                                                    },
                                                                            }}
                                                                        >
                                                                            <TableCell>
                                                                                <Box>
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        fontWeight="medium"
                                                                                    >
                                                                                        {
                                                                                            item.item_name
                                                                                        }
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        color="textSecondary"
                                                                                    >
                                                                                        {
                                                                                            item.item_code
                                                                                        }
                                                                                    </Typography>
                                                                                </Box>
                                                                            </TableCell>
                                                                            <TableCell align="center">
                                                                                <TextField
                                                                                    type="number"
                                                                                    size="small"
                                                                                    value={
                                                                                        item.qty
                                                                                    }
                                                                                    onChange={(
                                                                                        e
                                                                                    ) =>
                                                                                        updateItemQty(
                                                                                            item.item_code,
                                                                                            e
                                                                                                .target
                                                                                                .value
                                                                                        )
                                                                                    }
                                                                                    inputProps={{
                                                                                        min: 0,
                                                                                        step: 0.1,
                                                                                    }}
                                                                                    sx={{
                                                                                        width: 70,
                                                                                    }}
                                                                                    variant="outlined"
                                                                                    placeholder="0"
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell align="center">
                                                                                <TextField
                                                                                    type="number"
                                                                                    size="small"
                                                                                    value={
                                                                                        item.rate
                                                                                    }
                                                                                    onChange={(
                                                                                        e
                                                                                    ) =>
                                                                                        updateItemRate(
                                                                                            item.item_code,
                                                                                            e
                                                                                                .target
                                                                                                .value
                                                                                        )
                                                                                    }
                                                                                    inputProps={{
                                                                                        min: 0,
                                                                                        step: 0.01,
                                                                                    }}
                                                                                    sx={{
                                                                                        width: 90,
                                                                                    }}
                                                                                    variant="outlined"
                                                                                    placeholder="0.00"
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell align="right">
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    fontWeight="medium"
                                                                                >
                                                                                    ₪
                                                                                    {item.amount.toFixed(
                                                                                        2
                                                                                    )}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell align="center">
                                                                                <Tooltip title="Remove item from purchase">
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={() =>
                                                                                            removeItemFromStockEntry(
                                                                                                item.item_code
                                                                                            )
                                                                                        }
                                                                                        color="error"
                                                                                        sx={{
                                                                                            "&:hover":
                                                                                                {
                                                                                                    bgcolor:
                                                                                                        "error.light",
                                                                                                    color: "white",
                                                                                                },
                                                                                        }}
                                                                                    >
                                                                                        <Remove />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    )
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>

                                                    {/* Total and Paid Amount Section */}
                                                    <Box
                                                        sx={{
                                                            mt: 2,
                                                            p: 2,
                                                            bgcolor:
                                                                "primary.50",
                                                            borderRadius: 1,
                                                            border: "1px solid",
                                                            borderColor:
                                                                "primary.200",
                                                        }}
                                                    >
                                                        <Grid
                                                            container
                                                            spacing={2}
                                                            alignItems="center"
                                                        >
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                sm={6}
                                                            >
                                                                <Typography
                                                                    variant="h6"
                                                                    color="primary"
                                                                    fontWeight="bold"
                                                                >
                                                                    Total: ₪
                                                                    {formState.stockEntryTotal.toFixed(
                                                                        2
                                                                    )}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                sm={6}
                                                            >
                                                                <TextField
                                                                    label="Paid Amount"
                                                                    type="number"
                                                                    value={
                                                                        formState.purchasePaidAmount
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        formState.setPurchasePaidAmount(
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    fullWidth
                                                                    variant="outlined"
                                                                    size="small"
                                                                    inputProps={{
                                                                        min: 0,
                                                                        step: 0.01,
                                                                        max: formState.stockEntryTotal,
                                                                    }}
                                                                    helperText={
                                                                        parseFloat(
                                                                            formState.purchasePaidAmount
                                                                        ) <
                                                                        formState.stockEntryTotal
                                                                            ? `Outstanding: ₪${(
                                                                                  formState.stockEntryTotal -
                                                                                  parseFloat(
                                                                                      formState.purchasePaidAmount ||
                                                                                          0
                                                                                  )
                                                                              ).toFixed(
                                                                                  2
                                                                              )}`
                                                                            : "Full payment"
                                                                    }
                                                                    FormHelperTextProps={{
                                                                        sx: {
                                                                            fontWeight:
                                                                                "medium",
                                                                            color:
                                                                                parseFloat(
                                                                                    formState.purchasePaidAmount
                                                                                ) <
                                                                                formState.stockEntryTotal
                                                                                    ? "warning.main"
                                                                                    : "success.main",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        )}

                                        {formState.selectedItems.length ===
                                            0 && (
                                            <Box
                                                sx={{
                                                    mt: 3,
                                                    p: 3,
                                                    textAlign: "center",
                                                    bgcolor: "primary.50",
                                                    borderRadius: 2,
                                                    border: "1px solid",
                                                    borderColor: "primary.200",
                                                }}
                                            >
                                                <ArrowUpward
                                                    sx={{
                                                        fontSize: 32,
                                                        color: "primary.main",
                                                        mb: 1,
                                                    }}
                                                />
                                                <Typography
                                                    variant="h6"
                                                    color="primary.main"
                                                    gutterBottom
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    Start Adding Items
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mb: 1 }}
                                                >
                                                    Search for items using the
                                                    field above, or click
                                                    "Create Item" to add new
                                                    products
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontStyle: "italic",
                                                        opacity: 0.8,
                                                    }}
                                                >
                                                    💡 Tip: Search will clear
                                                    automatically after adding
                                                    each item
                                                </Typography>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </>
                        )}

                        {/* Employee Advance Tab */}
                        {formState.currentTab === 2 && (
                            <EmployeeAdvanceTab
                                selectedEmployee={formState.selectedEmployee}
                                setSelectedEmployee={
                                    formState.setSelectedEmployee
                                }
                                employees={dataState.employees}
                                advanceAmount={formState.advanceAmount}
                                setAdvanceAmount={formState.setAdvanceAmount}
                                advanceReason={formState.advanceReason}
                                setAdvanceReason={formState.setAdvanceReason}
                            />
                        )}

                        <LoadingButton
                            type="submit"
                            variant="contained"
                            color="primary"
                            loading={submitLoading}
                            disabled={!formValid}
                            sx={{ mt: 2 }}
                        >
                            Submit{" "}
                            {formState.currentTab === 0
                                ? "Expense"
                                : formState.currentTab === 1
                                ? formState.isPaymentMode
                                    ? "Payment"
                                    : "Purchase"
                                : "Employee Advance"}
                        </LoadingButton>
                    </Box>
                </div>
            </div>

            {/* Create Item Modal */}
            <Dialog
                open={formState.showCreateItemModal}
                onClose={() => formState.setShowCreateItemModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Create New Item</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <TextField
                            label="Item Name *"
                            value={formState.newItemData.item_name}
                            onChange={(e) =>
                                formState.setNewItemData((prev) => ({
                                    ...prev,
                                    item_name: e.target.value,
                                }))
                            }
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            placeholder="Enter item name"
                        />

                        <Autocomplete
                            options={dataState.itemGroups || []}
                            getOptionLabel={(option) =>
                                option.item_group_name || option.name
                            }
                            value={
                                dataState.itemGroups?.find(
                                    (group) =>
                                        group.name ===
                                        formState.newItemData.item_group
                                ) || null
                            }
                            onChange={(event, newValue) => {
                                formState.setNewItemData((prev) => ({
                                    ...prev,
                                    item_group: newValue?.name || "",
                                }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Item Group *"
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    placeholder="Select item group"
                                />
                            )}
                        />

                        <TextField
                            label="Purchase Rate *"
                            type="number"
                            value={formState.newItemData.purchase_rate}
                            onChange={(e) =>
                                formState.setNewItemData((prev) => ({
                                    ...prev,
                                    purchase_rate: e.target.value,
                                }))
                            }
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            inputProps={{ min: 0, step: 0.01 }}
                            placeholder="Enter purchase rate"
                        />

                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                <strong>Default Settings:</strong>
                                <br />
                                • Purchase Item: Enabled
                                <br />
                                • Sales Item: Disabled
                                <br />
                                • Stock UOM: Nos
                                <br />• Warehouse:{" "}
                                {dataState.company.default_warehouse ||
                                    "Default"}
                            </Typography>
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => formState.setShowCreateItemModal(false)}
                        disabled={formState.creatingItem}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={handleCreateItem}
                        loading={formState.creatingItem}
                        variant="contained"
                        disabled={
                            !formState.newItemData.item_name ||
                            !formState.newItemData.item_group ||
                            !formState.newItemData.purchase_rate
                        }
                    >
                        Create Item
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default CashOutDialog;
